import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { MatLegacyMenuTrigger } from '@angular/material/legacy-menu';
import { capraIconChartExplorer } from '@mode/capra';
import { ChartTypes, FeatureFlag } from '@mode/shared/contract-common';
import { trackByFactory } from '@mode/capra';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { getInitialFlagValue, ConvertToVETooltipMap } from '@mode/shared/util-js';

export interface ChartOptionDefinition {
  id: ChartTypes.ChartType | 'legacyChart';
  name: string;
  icon: string;
  pickerText: string;
  disabled?: boolean;
  hidden?: boolean;
}

enum ChartTypeCategory {
  VizExplorer = 'Visual Explorer',
  QuickChart = 'Quick charts',
}

@Component({
  selector: 'mode-visualization-menu',
  templateUrl: './visualization-menu.component.html',
  styleUrls: ['./visualization-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'chartPicker',
})
export class VisualizationMenuComponent implements OnChanges {
  static $inject = ['AnalyticsService', 'FeatureFlagsFacade'];

  // Setting TriggerTypes Enum to a Class property for use in the template
  public readonly TriggerTypes: typeof ChartTypes.TriggerTypes = ChartTypes.TriggerTypes;
  @Input() chartType: ChartTypes.ChartType | undefined;
  @Input() isChartTypeConvertibleToVizExplorer = false;
  @Input() triggerType!: string;
  @Input() overlapTrigger?: boolean | undefined;
  @Input() disableTrigger = false;
  @Input() menuTriggerClicked?: boolean;
  @Input() isLocal?: boolean;
  @Input() reportId?: string;
  @Input() showV1ChartOption = false;
  @Output() chartTypeSelected: EventEmitter<ChartTypes.ChartType | 'legacyChart'> = new EventEmitter<
    ChartTypes.ChartType | 'legacyChart'
  >();
  @Output() menuClosed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() disabledLegacyVizTooltipText = '';
  @Input() disableLegacyViz = false;

  @ViewChild(MatLegacyMenuTrigger) triggerBtn!: MatLegacyMenuTrigger;

  showHistogram = getInitialFlagValue(FeatureFlag.HistogramQuickChart) as unknown as boolean;

  vegasChartType: ChartOptionDefinition = {
    id: 'vegas',
    name: 'Visual Explorer',
    icon: 'illo-visual-explorer',
    pickerText: '',
  };

  chartTypes: ReadonlyArray<ChartOptionDefinition> = [
    { id: 'table', name: 'Table', icon: 'illo-chart-thumb-table', pickerText: 'Table' },
    {
      id: 'vegasPivotTable',
      name: 'Pivot Table',
      icon: 'illo-chart-thumb-pivot-table',
      pickerText: 'Pivot Table',
    },
    { id: 'bigValue', name: 'Big Value', icon: 'illo-chart-thumb-bigvalue', pickerText: 'Big Value' },
    { id: 'line', name: 'Line', icon: 'illo-chart-thumb-line', pickerText: 'Line' },
    { id: 'linePlusBar', name: 'Line + Bar', icon: 'illo-chart-thumb-line-bar', pickerText: 'Line + Bar' },
    { id: 'stackedBar', name: 'Bar', icon: 'illo-chart-thumb-bar-vert', pickerText: 'Bar' },
    { id: 'stackedBar100', name: '100% Bar', icon: 'illo-chart-thumb-bar-100-vert', pickerText: '100% Bar' },
    { id: 'bar', name: 'Grouped Bar', icon: 'illo-chart-thumb-grouped-bar-vert', pickerText: 'Grouped Bar' },
    { id: 'area', name: 'Area', icon: 'illo-chart-thumb-area', pickerText: 'Area' },
    { id: 'hStackedBar', name: 'Bar', icon: 'illo-chart-thumb-bar-horiz', pickerText: 'Bar' },
    { id: 'hStackedBar100', name: '100% Bar', icon: 'illo-chart-thumb-bar-100-horiz', pickerText: '100% Bar' },
    { id: 'hBar', name: 'Grouped Bar', icon: 'illo-chart-thumb-grouped-bar-horiz', pickerText: 'Grouped Bar' },
    { id: 'totalArea', name: '100% Area', icon: 'illo-chart-thumb-area-100', pickerText: '100% Area' },
    { id: 'scatter', name: 'Scatter Plot', icon: 'illo-chart-thumb-scatterplot', pickerText: 'Scatter' },
    { id: 'pie', name: 'Pie', icon: 'illo-chart-thumb-pie', pickerText: 'Pie' },
    { id: 'donut', name: 'Donut', icon: 'illo-chart-thumb-donut', pickerText: 'Donut' },
    { id: 'histogram', name: 'Histogram', icon: 'illo-chart-thumb-histogram', pickerText: 'Histogram' },
  ];

  readonly legacyChartTypes: Array<ChartOptionDefinition> = [
    {
      id: 'legacyChart',
      name: 'Legacy Chart',
      icon: 'illo-chart-thumb-legacy-chart',
      pickerText: 'Legacy Chart',
    },
  ];

  selectedChartCategory: ChartTypeCategory = ChartTypeCategory.VizExplorer;
  selectedChartType: ChartOptionDefinition | undefined = {} as ChartOptionDefinition;
  selectedIcon = '';
  selectedChartName = '';
  visualExplorerTooltip = '';

  trackByFn = trackByFactory<ChartOptionDefinition>(({ id }) => id);

  // Capra tooltip config
  readonly tooltipInteractive = false;

  constructor(private analyticsService: AnalyticsService) {}

  ngOnInit() {
    if (this.showHistogram === false) {
      this.hideHistogram();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // disable legacy charts based on input
    if (changes.disableLegacyViz) {
      this.updateDisabledLegacyChartTypes();
    }

    if (changes.showV1ChartOption && !this.showV1ChartOption) {
      this.hideV1ChartsOption();
    }

    if (changes.chartType) {
      const chartType: ChartTypes.ChartType = changes.chartType.currentValue;
      if (chartType === 'vegas') {
        this.selectedChartType = this.vegasChartType;
        this.selectedChartCategory = ChartTypeCategory.VizExplorer;
        this.selectedChartName = '';
        this.selectedIcon = this.vegasChartType.icon;
      } else {
        this.selectedChartType = this.chartTypes.find((chart) => chart.id === chartType);
        this.selectedChartCategory = ChartTypeCategory.QuickChart;
        this.selectedIcon = this.selectedChartType?.icon || '';
        this.selectedChartName = this.selectedChartType?.name || '';
      }
    }
    this.visualExplorerTooltip = this.makeVisualExplorerTooltip();

    if (changes.menuTriggerClicked?.currentValue) {
      this.openPopOverMenu();
    }
  }

  updateDisabledLegacyChartTypes() {
    this.legacyChartTypes.forEach((chartItem) => {
      chartItem.disabled = this.disableLegacyViz;
      chartItem.name = chartItem.disabled ? this.disabledLegacyVizTooltipText : 'Legacy Chart';
    });
  }

  hideHistogram() {
    const histogram = this.chartTypes.find((chart) => chart.id === 'histogram');
    if (histogram) histogram.hidden = true;
  }

  hideV1ChartsOption() {
    const legacyChartType = this.legacyChartTypes.find((chart) => chart.id === 'legacyChart');
    if (legacyChartType) legacyChartType.hidden = true;
  }

  hasLegacyChart() {
    return this.legacyChartTypes.some((chart) => !chart.hidden);
  }

  openPopOverMenu() {
    this.triggerBtn.openMenu();
  }

  selectVizExplorer(): void {
    this.chartTypeSelected.emit(this.vegasChartType.id);
  }

  selectChart(chart: ChartOptionDefinition): void {
    this.chartTypeSelected.emit(chart.id);

    if (!this.isLocal) {
      this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.MenuItemSelected, {
        location: AnalyticsEvent.SubContext.QueryNav,
        context: AnalyticsEvent.Context.Report,
        context_id: this.reportId,
        action: 'dataset new chart',
        menu_selection: chart,
      });
    }
  }

  onMenuClosed() {
    this.menuClosed.emit(false);
  }

  getTooltipDescription() {
    const tooltip = ConvertToVETooltipMap[this.chartType as keyof typeof ConvertToVETooltipMap];
    if (tooltip) {
      return tooltip;
    }
    return this.isChartTypeConvertibleToVizExplorer
      ? 'Enhance your charts with facets, dual axes, custom colors, and much more'
      : 'Create advanced charts with facets, dual axes, and much more';
  }

  makeVisualExplorerTooltip() {
    const iconData = capraIconChartExplorer.data;
    const caption = this.isChartTypeConvertibleToVizExplorer ? 'Switch to Visual Explorer' : 'Custom chart builder';
    const description = this.getTooltipDescription();
    return `
    <div class='visualization-menu-tooltip'>
      <span class='viz-explorer-caption'>${caption}</span>
      <div class='viz-explorer-description'>
        ${iconData}
        <span>${description}</span>
      </div>
    </div>`;
  }
}
